import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  HStack,
  Text,
  VStack,
  Heading,
  Icon,
  useMediaQuery,
  Flex,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  FursatookImageGallery,
  GALLERY_LAYOUTS,
} from "../image-gallery";
import { useState } from "react";
import { capitalize } from "../../utils/helpers";

import { TbAutomaticGearbox } from "react-icons/tb";
import { MdAirlineSeatReclineExtra } from "react-icons/md";
import { BsFuelPumpFill } from "react-icons/bs";
import { FaCar } from "react-icons/fa6";

const CARD_WIDTH = { base: "100%", lg: "700px" };

/** Same pricing UX as accommodation cards: hero rate, period label, secondary total. */
const VehicleCardPrices = ({ pricePerDay, totalPrice, t }) => (
  <Box
    dir="ltr"
    flexShrink={0}
    textAlign="right"
    minW="min-content"
    maxW="100%"
  >
    <Flex
      align={{ base: "flex-end", sm: "baseline" }}
      justify="flex-end"
      flexDir={{ base: "column", sm: "row" }}
      gap={{ base: 0, sm: 1 }}
      rowGap={{ base: 0.5 }}
    >
      <Text
        as="span"
        fontWeight="700"
        fontSize={{ base: "xl", sm: "2xl" }}
        lineHeight="1.1"
        letterSpacing="tight"
        color="gray.800"
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {pricePerDay}
      </Text>
      <Text
        as="span"
        fontSize="xs"
        color="gray.600"
        fontWeight="normal"
        whiteSpace="nowrap"
        lineHeight="1.2"
      >
        {t("perDayLabel")}
      </Text>
    </Flex>
    <Text fontSize="xs" color="gray.600" mt={1} lineHeight="short">
      {t("bookingTotal")}{" "}
      <Text
        as="span"
        fontWeight="semibold"
        color="gray.700"
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {totalPrice}
      </Text>
    </Text>
  </Box>
);

const VehicleCard = ({ vehicle, onSelect, canBook = true }) => {
  const {
    id: vehicleId,
    name,
    numberOfSeats,
    vehicleType,
    fuelType,
    automatic,
    //productionYear,
    pricePerDay,
    totalPrice,
    //availableStock,
    images,
  } = vehicle;
  const { t, i18n } = useTranslation(["common", "vehicle"]);
  const [isLargerThan648] = useMediaQuery("(min-width: 648px)");
  const [isMakingReservation, setIsMakingReservation] = useState(false);

  const ReserveCarButton = ({ onClick }) => (
    <Button
      isLoading={isMakingReservation}
      onClick={onClick}
      colorScheme="teal"
      variant="solid"
      flexShrink={0}
      alignSelf={{ base: "flex-end", lg: "center" }}
      isDisabled={!canBook}
      title={!canBook ? t("reserveBlockedForRole") : undefined}
    >
      {t("reserveButtonText")}
    </Button>
  );

  const CarDetailsStackComponent = ({ children, ...props }) => {
    if (isLargerThan648) {
      return (
        <VStack alignItems="flex-start" alignSelf="flex-start" {...props}>
          {children}
        </VStack>
      );
    } else {
      return (
        <HStack {...props} flexWrap="wrap">
          {children}
        </HStack>
      );
    }
  };

  return (
    <Card
      className="vehicle-card"
      w={CARD_WIDTH}
      variant="outline"
      borderRadius="12px"
      overflow="hidden"
      display="flex"
      flexDir="column"
    >
      <CardBody as={VStack} padding={4} fontSize="small" align="stretch" flex={1}>
        <Heading fontSize="xl" mb={6} alignSelf="flex-start">
          {`${name}`}
        </Heading>
        <Flex
          justifyContent="space-between"
          gap={4}
          alignSelf="stretch"
          flexDirection={isLargerThan648 ? "row" : "column"}
          flex={1}
        >
          <FursatookImageGallery
            layout={GALLERY_LAYOUTS.vehicle}
            images={images}
            isRTL={i18n.language === "ar"}
          />
          <CarDetailsStackComponent alignSelf="flex-start">
            <HStack>
              <Icon as={FaCar}></Icon>
              <Text>
                {t(vehicleType, {
                  ns: "vehicle",
                  defaultValue: capitalize(vehicleType),
                })}
              </Text>
            </HStack>
            <HStack>
              <Icon as={TbAutomaticGearbox} />
              <Text>
                {automatic ? t("bookingAutomatic") : t("bookingManual")}
              </Text>
            </HStack>
            <HStack>
              <Icon as={MdAirlineSeatReclineExtra}></Icon>
              <Text>
                {t("vehicleSeatsCount", { count: numberOfSeats })}
              </Text>
            </HStack>
            <HStack>
              <Icon as={BsFuelPumpFill}></Icon>
              <Text>
                {t(fuelType, {
                  ns: "vehicle",
                  defaultValue: capitalize(fuelType),
                })}
              </Text>
            </HStack>
            {/*availableStock > 1 && (
              <Text color="teal.600" fontWeight="semibold">
                {`${availableStock} ${t("vehiclesInStock")}`}
              </Text>
            )*/}
          </CarDetailsStackComponent>
        </Flex>
      </CardBody>
      <CardFooter
        padding={4}
        pt={3}
        justify="space-between"
        align={{ base: "flex-end", lg: "center" }}
        flexWrap="wrap"
        gap={2}
        mt="auto"
      >
        <ReserveCarButton
          onClick={() => {
            setIsMakingReservation(true);
            onSelect(vehicleId);
          }}
        />
        <VehicleCardPrices
          pricePerDay={pricePerDay}
          totalPrice={totalPrice}
          t={t}
        />
      </CardFooter>
    </Card>
  );
};

export default VehicleCard;
